// Guided tour step definitions for the operator dashboard.
// Steps anchor to `data-testid` selectors that only render for tabs the user
// can access — filtering to on-screen elements makes every tour automatically
// role/permission-aware (RBAC).

// Each step: { element?, view?, group?, path?, openSettings?, closeSettings?, settingsTab?, deferMount?, popover }
// - `view` switches the dashboard tab (CustomEvent 'navigate') before highlight.
// - `group` opens ONLY that sidebar group (CustomEvent 'tour-set-group') —
//   accordion behavior: previous groups close as the tour sails on.
// - `path` (Intelligence tour) navigates the suite to that route first.
// - `openSettings` / `closeSettings` open/close the Settings dialog.
// - `settingsTab` switches the active tab inside Settings ('settings-set-tab').
// - `deferMount` keeps a step whose element mounts later (tab content, dialogs)
//   from being filtered out before the tour starts.
// - Steps without `element` render as a centered modal (intro/outro/narrative).

export const BASIC_TOUR_ID = 'basic_v1';
export const ADVANCED_TOUR_ID = 'advanced_v1';
export const INTEL_TOUR_PENDING_KEY = 'bf_intel_tour_pending';

const s = (title, description, extra = {}) => ({ title, description, ...extra });

// Returns a lazy element resolver that picks the first VISIBLE match — some
// anchors render twice (mobile + desktop) or only after tab content loads.
const firstVisible = (selector) => () => {
  const els = document.querySelectorAll(selector);
  for (const el of els) {
    if (el.offsetWidth > 0 || el.offsetHeight > 0) return el;
  }
  return els[0];
};

const settingsTabEl = (tabId) => firstVisible(`[data-testid="modal-nav-${tabId}"], [data-testid="settings-tab-btn-${tabId}"]`);

// The proper tour: every stop opens the real tab, then points at a feature or
// two inside it. Assets/crew assignment comes early — HELM depends on it.
export const getBasicTourSteps = () => ([
  {
    popover: s(
      '👋 Welcome to BookingFlows',
      "Let's take a proper tour, deck by deck — about two minutes. Each stop opens the real tab so you see exactly where things live. Skip anytime; replay from the ✨ sparkle on the right edge."
    ),
  },

  // ── BRIDGE ──────────────────────────────────────────────────────────────
  {
    element: '[data-testid="nav-overview"]',
    view: 'overview',
    group: 'main',
    popover: s('Manifest — your day at a glance', "Home base on the Bridge. Every trip and booking for the day lives here — check guests in, collect payment on unpaid bookings, and assign crew to trips.", { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="home-filter-btn"]'),
    deferMount: true,
    view: 'overview',
    group: 'main',
    popover: s('Filters & search, top right', 'Slice the manifest by service, status or crew — and the search glass next door finds any guest instantly. Tap any booking row to open its full detail sheet.', { side: 'bottom', align: 'end' }),
  },
  {
    element: '[data-testid="nav-schedule"]',
    view: 'schedule',
    group: 'main',
    popover: s('Calendar', 'All bookings laid out by date. Filter by status (including refunds) and spot busy vs. quiet days at a glance.', { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="create-booking-btn"]'),
    deferMount: true,
    view: 'schedule',
    group: 'main',
    popover: s('Add bookings by hand', 'Phone call? Walk-up at the dock? Create a booking here in seconds — it flows straight onto the Manifest.', { side: 'bottom', align: 'end' }),
  },
  {
    element: '[data-testid="nav-home"]',
    view: 'home',
    group: 'main',
    popover: s('Dashboard', 'Your key numbers — revenue, bookings and trends at a glance.', { side: 'right', align: 'start' }),
  },

  // ── DECK: assets first — HELM reports, capacity & crew rules key off them ─
  {
    element: '[data-testid="nav-assets"]',
    view: 'assets',
    group: 'operations',
    popover: s('Assets — the backbone', "Your vessels: boats, jet skis, gear. This matters early — capacity, overbooking protection, crew assignment and your HELM earnings reports all key off which asset ran which trip.", { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="assets-add-button"]'),
    deferMount: true,
    view: 'assets',
    group: 'operations',
    popover: s('Add a vessel, set its rules', "Add each asset here, then open it to configure the rules — capacity, and which ROLES are required to operate it. Those rules control who can be assigned to which trips. Deactivate a vessel that's out of service and it vanishes from availability everywhere.", { side: 'bottom', align: 'end' }),
  },
  {
    element: '[data-testid="nav-services"]',
    view: 'services',
    group: 'operations',
    popover: s('Services — what you sell', "Jet Ski hire, parasailing, charters. LINK each service to the assets that run it — that's how vessels get assigned to trips and capacity stays honest.", { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="add-service-btn"]'),
    deferMount: true,
    view: 'services',
    group: 'operations',
    popover: s('Create a service', 'Pricing, duration, add-ons, capacity and the cancellation policy — all set here. Changes save instantly and hit your booking widget immediately.', { side: 'bottom', align: 'end' }),
  },
  {
    element: '[data-testid="nav-teamMembers"]',
    view: 'teamMembers',
    group: 'operations',
    popover: s('Team — your crew', 'Invite staff and captains (they set their own password via the invite). Assign crew to trips from the Manifest — the roles you give them decide which vessels they can operate.', { side: 'right', align: 'start' }),
  },
  {
    element: '[data-testid="nav-roles"]',
    view: 'roles',
    group: 'operations',
    popover: s('Roles & permissions', 'Create roles and choose exactly which tabs each role sees — and remember, roles also gate who can operate which assets. Managers get broad access; crew stay focused.', { side: 'right', align: 'start' }),
  },

  // ── CUSTOMERS ───────────────────────────────────────────────────────────
  {
    element: '[data-testid="nav-waivers"]',
    view: 'waivers',
    group: 'customers',
    popover: s('Waivers', 'Your liability waiver lives here and versions automatically every time you edit. Guests sign digitally before the trip — UAE orgs sign bilingually in English & Arabic.', { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="waiver-preview-btn"]'),
    deferMount: true,
    view: 'waivers',
    group: 'customers',
    popover: s('Preview & walk-up QR', 'Preview exactly what guests see when they sign — and grab the walk-up QR code for the dock so day-of guests can sign on their own phone.', { side: 'bottom', align: 'end' }),
  },
  {
    element: firstVisible('[data-testid="walkup-sign-url"]'),
    deferMount: true,
    view: 'waivers',
    group: 'customers',
    popover: s('Your public signing link', "This is your walk-up signing page — /sign/yourcompany. Copy it anywhere or print the QR at the dock. And every customer ticket carries its own “Sign waiver” button, so guests who booked online sign from their phone before they ever reach you — the manifest shows who still owes one.", { side: 'top', align: 'start' }),
  },
  {
    element: '[data-testid="nav-discounts"]',
    view: 'discounts',
    group: 'customers',
    popover: s('Discounts', 'Create promo codes (percent or fixed). Later you can attach a code to a marketing campaign in the Intelligence Suite and track what it brings back.', { side: 'right', align: 'start' }),
  },

  // ── MARKETING ───────────────────────────────────────────────────────────
  {
    element: '[data-testid="nav-conversions"]',
    view: 'conversions',
    group: 'marketing',
    popover: s('Integrations & tracking', 'Wire up GA4, Google Ads, Meta Pixel and Zapier here so your other tools see every booking — and manage your BookingFlows Beacon.', { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="bf-beacon-status"], [data-testid="bf-beacon-id-input"]'),
    deferMount: true,
    view: 'conversions',
    group: 'marketing',
    popover: s('Your Beacon, live', "The Beacon streams your website's visitor events into HELM Intelligence. Its live status shows right here — and there's a one-click link to our Chrome extension (Beacon Tender) to verify it on any page of your site.", { side: 'top', align: 'start' }),
  },

  // ── SETTINGS + HELP ─────────────────────────────────────────────────────
  {
    element: '[data-testid="header-profile-menu"]',
    popover: s('Your profile menu', 'Notifications, settings and help all live under your avatar in the top right — branding, calendar sync, security, integrations and the Log book (your full audit trail) are inside Settings. The Advanced tour walks through them in depth.', { side: 'bottom', align: 'end' }),
  },
  {
    element: '[data-testid="tour-launcher-button"]',
    popover: s('Help, anytime', 'This button is home base: your setup checklist, full how-to guides, this tour, the Advanced one, and Bosun — your first mate. Tap it whenever you need a hand.', { side: 'left', align: 'end' }),
  },
  {
    popover: s("🎉 You're all set", "That's the core loop: vessels → services → crew → bookings. And there's plenty more aboard — Gift Cards, Reviews, Check-In QR, Flows automations, the Purser's P&L. Poke around, or Ask Bosun anytime. When you're ready, the Advanced tour unlocks your data & marketing tools."),
  },
]);

// The full command briefing: reports → Relations → Beacon in Conversions →
// the Settings engine room → the Beacon story → into Intelligence.
export const getAdvancedTourSteps = (isAdmin = true) => {
  const settingsAdminSteps = isAdmin ? [
    // (Webhooks moved out of Settings → now the Marketing → Webhooks tab)
    {
      element: settingsTabEl('settings-api-keys'),
      deferMount: true,
      settingsTab: 'settings-api-keys',
      popover: s('API Keys — handle with care', "Keys power your custom integrations. They're shown ONCE at creation, stored hashed, and every create or revoke is stamped in the Log book. Treat them like the keys to the engine room.", { side: 'right', align: 'start' }),
    },
  ] : [];

  return [
    {
      popover: s(
        '🚀 Advanced: command the whole ship',
        "You've got the basics down. This tour covers your reports, the Settings engine room, the Beacon, and ends with a full briefing inside the Intelligence Suite."
      ),
    },
    {
      element: '[data-testid="nav-reports"]',
      view: 'reports',
      group: 'financials',
      popover: s('Reports — what to buy next', 'In the Purser section: asset & performance reports show which services and vessels earn the most — so you know when it makes sense to buy that next jet ski.', { side: 'right', align: 'start' }),
    },
    {
      element: firstVisible('[data-testid="reports-controls"]'),
      deferMount: true,
      view: 'reports',
      group: 'financials',
      popover: s('Slice it your way', 'Date presets, accrual vs. cash basis, and filters by service or asset — this is where asset assignment from the Basic tour pays off.', { side: 'bottom', align: 'start' }),
    },
    {
      element: '[data-testid="nav-relations"]',
      view: 'relations',
      group: 'customers',
      popover: s('Relations — the baby version of Intelligence', "Every customer and lead is captured here automatically — think of it as your CRM. But this is just the surface: the real power is what the platform builds FROM this data. Stay with me.", { side: 'right', align: 'start' }),
    },
    {
      element: firstVisible('[data-testid="bf-beacon-status"], [data-testid="bf-beacon-id-input"]'),
      deferMount: true,
      view: 'conversions',
      group: 'marketing',
      popover: s('Your Beacon, checked live', "Over in Integrations, your Beacon status is verified in real time. It's a tiny script that streams website visitors into your data — and the Beacon Tender Chrome extension link right here lets you verify any page of your site yourself.", { side: 'top', align: 'start' }),
    },
    {
      element: '[data-testid="header-profile-menu"]',
      popover: s('Into the engine room', "Next stop: Settings — where the ship's vital systems live. It's under your avatar in the top right. Let's step inside.", { side: 'bottom', align: 'end' }),
    },
    {
      element: settingsTabEl('settings-calendar'),
      deferMount: true,
      openSettings: true,
      settingsTab: 'settings-calendar',
      popover: s('Calendar Sync', "Two-way sync your bookings with Google, Apple or Outlook so the whole crew's calendar always matches the manifest.", { side: 'right', align: 'start' }),
    },
    {
      element: settingsTabEl('settings-2fa'),
      deferMount: true,
      settingsTab: 'settings-2fa',
      popover: s('2FA Security', 'One-time codes lock down your account. Strongly recommended for owners and anyone with access to the Purser.', { side: 'right', align: 'start' }),
    },
    {
      element: settingsTabEl('settings-passkeys'),
      deferMount: true,
      settingsTab: 'settings-passkeys',
      popover: s('Passkeys', 'Passwordless sign-in with Face ID / Touch ID — faster than passwords and phishing-proof.', { side: 'right', align: 'start' }),
    },
    ...settingsAdminSteps,
    {
      element: settingsTabEl('settings-activity'),
      deferMount: true,
      settingsTab: 'settings-activity',
      popover: s('The Log book', 'Every action across your operation — edits, refunds, key creation, check-ins — timestamped and attributed. Nothing happens off the record.', { side: 'right', align: 'start' }),
    },
    {
      closeSettings: true,
      popover: s('📡 The BookingFlows Beacon', "The Beacon streams visitor events and customer data from your website into HELM Intelligence. It takes a minute to install — and if BookingFlows built or hosts your website, chances are it's already aboard. Verify any page with the Beacon Tender extension you just saw in Conversions."),
    },
    {
      popover: s('🎯 Data → Audiences → Campaigns', 'The Beacon and your bookings feed the audience engine automatically — past guests, warm visitors, big spenders. Retarget them with campaigns, attach a discount code, and close the loop: bookings → data → audiences → campaigns → more bookings.'),
    },
    {
      element: '[data-testid="nav-intelligence"]',
      navigateToIntel: true,
      popover: s('⚓ Now for the good part', "Everything you just saw feeds the Intelligence Suite. Click Done and I'll take you inside for the full briefing — the Chartroom, your website analytics, audiences and campaigns.", { side: 'right', align: 'start' }),
    },
  ];
};

// The campaign composer walk — shared by the suite tour and the "Run a
// campaign" guide. Includes the two hands-on stops (AI draft, inbox test).
const campaignWizardSteps = () => ([
  {
    wizard: { open: true },
    wizardStep: 0,
    element: firstVisible('[data-testid="wizard-details-input"]'),
    deferMount: true,
    popover: s('Draft one together — the goal', 'Say it in plain words: "bring back last summer\'s jet-ski customers with a weekday deal". The AI takes it from there.', { side: 'left', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 1,
    element: firstVisible('[data-testid^="wizard-segment-"]'),
    deferMount: true,
    popover: s('Pick who hears from you', "These are the audiences from earlier — past guests, warm leads, big spenders. Pick one. Only contacts who opted in to marketing are ever messaged, whatever you choose.", { side: 'left', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 2,
    element: firstVisible('[data-testid="wizard-budget-300"], [data-testid="wizard-budget-custom"]'),
    deferMount: true,
    popover: s('Your daily budget', "Start small — a couple of dollars (or dirhams) a day is plenty. You only pay per message actually sent, unused budget never rolls over, and the reach estimate right below updates live as you tune it.", { side: 'left', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 2,
    element: firstVisible('[data-testid="wizard-frequency-select"]'),
    deferMount: true,
    popover: s('How often each customer hears from you', "The frequency cap: no contact ever gets more than one message per this window — and every touch rotates to a different ad variation, so it never feels like spam.", { side: 'left', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 2,
    element: firstVisible('[data-testid="wizard-destination-select"]'),
    deferMount: true,
    popover: s('Where the link goes — choose one of your flows!', "Every ad's button lands HERE. Your flows are direct booking pages — perfect landing spots. Pick one, and every click is tagged so bookings trace straight back to this campaign. Full circle: data → audience → ad → your flow → booking.", { side: 'left', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 3,
    element: firstVisible('[data-testid="wizard-ai-draft-btn"]'),
    deferMount: true,
    advanceOn: 'intel-tour-ai-drafted',
    popover: s('✨ Give it a try!', "Don't just look — actually click “Draft ad variations with AI”. The AI reads your goal and writes several distinct ads on the spot (attach a promo code above and it weaves the code in too). Go on, click it — I'll wait right here and move us along the moment your ads are ready.", { side: 'left', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 3,
    element: firstVisible('[data-testid="wizard-variants-list"]'),
    deferMount: true,
    popover: s('Fresh off the press 🎉', "Look at that — every ad takes a different angle. Each contact gets a different variation on every touch, and delivery auto-weights toward whichever ads earn the most clicks. Discard any you don't like, edit them by hand, or draft more.", { side: 'left', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 4,
    element: firstVisible('[data-testid="wizard-test-send-block"]'),
    deferMount: true,
    advanceOn: 'intel-tour-test-sent',
    popover: s('📬 See them in your inbox first', "One more hands-on step: type YOUR email and hit “Email me these ads”. You'll receive every variation exactly as your guests would — real inbox, no “[Ad]” tags. Go ahead, send it — I'll wait.", { side: 'top', align: 'start' }),
  },
  {
    wizard: { open: true },
    wizardStep: 4,
    element: firstVisible('[data-testid="wizard-test-sent-card"]'),
    deferMount: true,
    popover: s('Sent! Go peek at your inbox 🎉', "That's exactly what lands for every guest — real first names, your tracked booking link, and a fresh variation on every touch. Have a look at your inbox, then hit Next.", { side: 'top', align: 'center' }),
  },
  {
    wizard: { open: true },
    wizardStep: 4,
    dismissTestModal: true,
    element: firstVisible('[data-testid="wizard-save-draft-btn"]'),
    deferMount: true,
    popover: s('Save as Draft', 'Then save it as a draft — nothing sends and nothing spends until YOU hit Launch.', { side: 'top', align: 'end' }),
  },
]);

// The Intelligence Suite briefing — also the Advanced tour's landing. Steps
// with `path` navigate the suite to the real page; `wizard`/`wizardStep` drive
// the campaign composer so the tour walks users right up to launching one.
// `prefix` targets the mobile drawer nav ('drawer-') vs. the desktop sidebar.
export const getIntelTourSteps = (prefix = '') => ([
  {
    popover: s('🧭 Welcome aboard HELM Intelligence', 'Your briefing, captain: this suite turns raw bookings and website traffic into clear decisions. Everything in here builds itself as you operate — no setup, no spreadsheets.'),
  },
  {
    element: `[data-testid="${prefix}intelligence-nav-asset-earnings"]`,
    path: '/intelligence/asset-earnings',
    popover: s('The Chartroom', 'Your live reports — this one shows what each vessel really earns, built from the asset assignments you saw earlier. Revenue Mix, Demand Center and Customer Insights sit alongside it.', { side: 'right', align: 'start' }),
  },
  {
    element: `[data-testid="${prefix}intelligence-nav-website-analytics"]`,
    path: '/intelligence/website-analytics',
    popover: s('Acquisition — Website Analytics', 'This is your website analytics tab — live visitors, page views and booking intent from your own site. No analytics degree required.', { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="beacon-live-strip"], [data-testid="beacon-setup"]'),
    deferMount: true,
    popover: s('Your Beacon — see this tag', "This is the Beacon: the tiny tag that streams your site's visitors into these reports. Live means it's aboard and reporting. Not installed — or want proof? Get the Beacon Tender extension from the Chrome Web Store, open your website, and watch the events fire in real time.", { side: 'bottom', align: 'start' }),
  },
  {
    element: `[data-testid="${prefix}intelligence-nav-audiences"]`,
    path: '/intelligence/audiences',
    popover: s('Audiences', 'Your customers and site visitors, aggregated into ready-to-use audiences — past guests, warm leads, big spenders. They build themselves from your bookings and Beacon data.', { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid^="audience-segment-"]'),
    deferMount: true,
    popover: s('Take this one', 'Each segment updates live — size, reachable contacts, consent all handled. Zero list-building. Every one of these is ready to campaign to.', { side: 'bottom', align: 'start' }),
  },
  {
    element: `[data-testid="${prefix}intelligence-nav-campaigns"]`,
    path: '/intelligence/campaigns',
    popover: s('Campaigns — where audiences become revenue', "Send campaigns straight to those audiences. Let's draft one together — it takes about a minute.", { side: 'right', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="camp-kpi-balance"]'),
    deferMount: true,
    popover: s('Your starter credit is on us ⚓', "See the balance? We've loaded starter ad credit — in your local currency, dollars or dirhams — so you can give it a real try. Go get some clicks: advertising to your own data costs a tiny fraction of typical ad spend.", { side: 'bottom', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid="auto-recharge-status"], [data-testid="add-credits-btn"]'),
    deferMount: true,
    popover: s('Set auto-recharge, stay conservative', "Flip on auto-recharge so campaigns never stall — and keep budgets modest. A couple of dollars (or dirhams) a day is plenty; our optimizer decides who gets what and when. No need to go crazy.", { side: 'bottom', align: 'start' }),
  },
  {
    element: firstVisible('[data-testid^="campaign-analytics-toggle-"], [data-testid^="campaign-variant-stats-"]'),
    deferMount: true,
    popover: s('Every ad reports back', "Each campaign card opens into full analytics — per-ad rankings, views, clicks, CTR and the optimizer's delivery split. Ads that earn clicks automatically get sent more. Once you launch, check back here to see which angles win.", { side: 'top', align: 'start' }),
  },
  ...campaignWizardSteps(),
  {
    wizard: { open: false },
    popover: s('⚓ You have the helm', "That's the whole loop: Beacon → audiences → campaigns → more bookings. Your starter credit is loaded — draft a real one now and go get some clicks. Smooth sailing!"),
  },
]);

// ─── How-to Guides — short, hands-on task walkthroughs ─────────────────────
// Each guide teaches one "lego chain" of features working together. Steps with
// `advanceOnSelector` hide Next and wait until the user actually DOES the thing
// (a visible match appears — or with `advanceOnAdded`, a NEW match appears).
export const getGuides = () => ([
  {
    id: 'guide_crew_v1',
    title: 'Set up your crew — roles & team',
    blurb: 'Where the system starts: a role, its permissions, your people.',
    steps: [
      { popover: s('⚓ Start on the Deck', "Everything keys off two things: ROLES decide what people can see and do, TEAM MEMBERS carry those roles. As the owner you always have full access — roles shape everyone else. Let's build both, hands-on.") },
      {
        element: '[data-testid="nav-roles"]', view: 'roles', group: 'operations',
        popover: s('Roles — positions with permissions', 'A role is a position at your company: Captain, Guide, Front-desk. Each one controls which tabs are visible and what its members can do inside them.', { side: 'right', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="roles-add-button"]'), deferMount: true, view: 'roles', group: 'operations',
        advanceOnSelector: '[data-testid="role-create-modal"]',
        popover: s('✨ Create one — click Add', "Make your first role now. Something real, like Captain. I'll wait.", { side: 'bottom', align: 'end' }),
      },
      {
        element: firstVisible('[data-testid="role-create-modal"]'), deferMount: true,
        popover: s('Name it, then the permissions', "The whole modal is yours — I'll stay out of the way. Name the role, then work the PERMISSIONS: for each tab you decide whether this role can see it, and whether it can create, edit or delete inside it. That one screen is the platform's entire security model. Set a few basics and save — then hit Next.", { side: 'left', align: 'start' }),
      },
      {
        element: '[data-testid="nav-teamMembers"]', view: 'teamMembers', group: 'operations',
        popover: s('Now the people', 'Team members are your staff — each gets their own login and sees only what their role allows.', { side: 'right', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="add-team-member-button"]'), deferMount: true, view: 'teamMembers', group: 'operations',
        advanceOnSelector: '[data-testid="add-team-member-modal"]',
        popover: s('✨ Add a team member — click it', "Add your first crew member (it can even be a test one). Go ahead — I'll wait.", { side: 'bottom', align: 'end' }),
      },
      {
        element: firstVisible('[data-testid="add-team-member-modal"]'), deferMount: true,
        popover: s('Attach the role — that\'s the key', "Walk the steps: name and contact, then the ROLE step — attach the role you just made. That's the connection that unlocks their view of the system. Set a simple password on the way; you can test-drive it later by signing out and signing in with your org shortname + their email. Finish and save, then Next.", { side: 'left', align: 'start' }),
      },
      { popover: s('⚓ Crew, set', "Roles gate everything — tabs, trips, even which vessels someone can operate. Adjust a role once and every member updates instantly. Your owner account always keeps full access.") },
    ],
  },
  {
    id: 'guide_new_asset_v1',
    title: 'You bought a new vessel',
    blurb: 'Register it, set crew rules, link it to services.',
    steps: [
      { popover: s('🚤 New vessel aboard', "Three stops to make a new asset earn: register it, gate who can operate it, and link it to the services it runs.") },
      {
        element: firstVisible('[data-testid="assets-add-button"]'), deferMount: true, view: 'assets', group: 'operations',
        advanceOnSelector: '[data-testid="add-asset-modal"]',
        popover: s('✨ Register it — click Add', "Click the add button and put your new vessel on the books. I'll wait.", { side: 'bottom', align: 'end' }),
      },
      {
        element: firstVisible('[data-testid="add-asset-modal"]'), deferMount: true,
        popover: s('Capacity & roles allowed — the two that matter', "The modal's all yours. Name and photos, sure — but nail these two: CAPACITY (get it right — it's exactly how the system knows never to overbook your services) and ROLES ALLOWED TO OPERATE — pick one or many (Captain, First Mate…). Only crew holding those roles can ever be assigned its trips. Work through it and save, then Next.", { side: 'left', align: 'start' }),
      },
      {
        element: '[data-testid="nav-roles"]', view: 'roles', group: 'operations',
        popover: s('Sanity-check your roles', "Make sure a role exists that matches what the vessel requires (e.g. Captain). No matching role on a crew member = they won't appear for assignment.", { side: 'right', align: 'start' }),
      },
      {
        element: '[data-testid="nav-services"]', view: 'services', group: 'operations',
        popover: s('Last piece — link it to services', 'Open the service this vessel runs and tick the new asset. Capacity stays honest, double-bookings stay impossible, and HELM starts reporting its earnings.', { side: 'right', align: 'start' }),
      },
      { popover: s('⚓ Wired in', "That's it: registered → crew-gated → linked. The vessel now shows up for trip assignment, availability, and the Chartroom's asset earnings report.") },
    ],
  },
  {
    id: 'guide_first_service_v1',
    title: 'Create your first service',
    blurb: 'Waiver + Stripe first, then the thing customers buy.',
    steps: [
      { popover: s('🛠️ The thing you sell', "A service is the bookable experience — pricing, duration, capacity. Two things make it money-ready first: your waiver and Stripe. Quick look at both, then we build.") },
      {
        element: '[data-testid="nav-waivers"]', view: 'waivers', group: 'customers',
        popover: s('First — your waiver', 'Guests sign this digitally before the trip. Set your waiver text once here; it versions itself automatically every time you edit.', { side: 'right', align: 'start' }),
      },
      {
        element: '[data-testid="nav-payments"]', view: 'payments', group: 'financials',
        popover: s('Second — connect Stripe', "Payments land in YOUR Stripe account — we never hold your money. You can build services without it, but checkout can't complete until the system knows where the money goes. Connect it here whenever you're ready.", { side: 'right', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="add-service-btn"]'), deferMount: true, view: 'services', group: 'operations',
        advanceOnSelector: '[data-testid="new-service-option"]',
        popover: s('✨ Now build it — click Add', "Go on, click the Add button — I'll wait.", { side: 'bottom', align: 'end' }),
      },
      {
        element: firstVisible('[data-testid="new-service-option"]'), deferMount: true,
        advanceOnSelector: '[data-testid="service-builder-modal"]',
        popover: s('…and “New Service”', 'Click it to open the builder.', { side: 'left', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="service-builder-modal"]'), deferMount: true,
        popover: s('The builder walks you through it', "It's all yours — I'll stand aside. Step by step: name & description (the AI can draft it), pricing model, duration & capacity, and LINK the asset that runs it — that link is what keeps availability honest. Take your time, finish and save — then hit Next.", { side: 'left', align: 'start' }),
      },
      { popover: s('⚓ Service, built', "It's live in your catalog and wired to an asset. Next guide: put it in front of customers with your first flow.") },
    ],
  },
  {
    id: 'guide_first_flow_v1',
    title: 'Launch your first flow',
    blurb: 'Your storefront: direct URL, embed, QR — share it anywhere.',
    steps: [
      { popover: s('🚪 One flow, many doors', "A flow is how customers reach your services — a booking page you can link, embed, or print as a QR. Let's make one and grab all three.") },
      {
        element: firstVisible('[data-testid="new-flow-btn"], [data-testid="create-first-flow-btn"]'), deferMount: true, view: 'flows', group: 'operations',
        advanceOnSelector: '[data-testid="flow-create-modal"]',
        popover: s('✨ Create a flow — click it!', "Open the flow builder. I'll wait right here.", { side: 'bottom', align: 'end' }),
      },
      {
        element: firstVisible('[data-testid="flow-create-modal"]'), deferMount: true,
        popover: s('Pick what it sells', 'Tick the services this flow offers and name it — one flow per website, ad, or partner works great. Save it, then Next.', { side: 'left', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="flow-copy-code-btn"]'), deferMount: true, view: 'flows', group: 'operations',
        popover: s('Your Direct URL — front and center', "Copy it and share it anywhere: text it, WhatsApp it, drop it in a DM or bio, hand it to a walk-up on the dock. Anyone who opens it can book and pay on the spot.", { side: 'top', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="flow-code-mode-embed"]'), deferMount: true, view: 'flows', group: 'operations',
        popover: s('Want it on your website?', 'Flip to Embed Code — one snippet and the whole booking experience lives inside your own site.', { side: 'top', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="flow-qr-btn"]'), deferMount: true, view: 'flows', group: 'operations',
        popover: s('And the QR', 'Download it, print it, make a sign for the dock or the shop counter. Scan → book.', { side: 'top', align: 'start' }),
      },
      { popover: s('⚓ Flow, launched', 'Link, embed, QR — every visit and booking through this flow is tracked, and flows make perfect landing pages for campaigns later. Next guide: go through it yourself, like a customer.') },
    ],
  },
  {
    id: 'guide_customer_exp_v1',
    title: 'Book like a customer',
    blurb: 'A 99% test code, a real checkout, your booking on the Manifest.',
    steps: [
      { popover: s('🛒 See what your customers see', "The fastest way to trust the system: book yourself, end to end. First, make yourself a deal so the test costs next to nothing.") },
      {
        element: firstVisible('[data-testid="add-discount-code-btn"]'), deferMount: true, view: 'discounts', group: 'customers',
        advanceOnSelector: '[data-testid="discount-create-modal"]',
        popover: s('✨ Create a test code — click Add', "Something like TEST99 at 99% off, with a low usage cap. Click it — I'll wait.", { side: 'bottom', align: 'end' }),
      },
      {
        element: firstVisible('[data-testid="discount-create-modal"]'), deferMount: true,
        popover: s('99% off, capped', 'Set the percent, cap the uses so it can\'t leak, save it. Then Next.', { side: 'left', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="flow-copy-code-btn"]'), deferMount: true, view: 'flows', group: 'operations',
        popover: s('Grab your flow link', "Copy your flow's Direct URL — that's your storefront door.", { side: 'top', align: 'start' }),
      },
      {
        popover: s('🧾 Now check out', "Open the link in a new tab and book yourself: pick the service and time, apply your test code at checkout — it'll cost next to nothing. (No Stripe connected yet? The payment step won't complete — that's fine, you'll still feel the whole flow.) Come back when you're done — Next when ready."),
      },
      {
        element: firstVisible('[data-testid="home-activity"]'), deferMount: true, view: 'overview', group: 'main',
        popover: s('📥 It lands here', "Bookings hit the Manifest the moment they happen — each row shows the guest, trip and its payment state.", { side: 'top', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid^="home-slot-"]'), deferMount: true, view: 'overview', group: 'main',
        advanceOnSelector: '[data-testid="home-detail-sheet"]',
        popover: s('✨ Open your booking', "Tap the row — let's look inside. I'll wait.", { side: 'bottom', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="home-detail-sheet"]'), deferMount: true,
        popover: s('Close the loop', 'Assign a vessel and crew (even yourself), collect a balance, resend the ticket — everything about the trip lives in this sheet.', { side: 'left', align: 'start' }),
      },
      { popover: s('⚓ End to end, done', "Flow → checkout → Manifest → assignment. You've lived the customer's side AND the operator's side. Tidy up: flip that 99% code off in Discounts when you're done playing.") },
    ],
  },
  {
    id: 'guide_run_the_day_v1',
    title: 'Run the day — crew, vessels & check-in',
    blurb: 'Assign a vessel & crew to a trip, then check guests in.',
    steps: [
      { popover: s('🧭 The daily loop', "This is the operator's day: manifest → assign → check in. Two of these stops are hands-on.") },
      {
        element: firstVisible('[data-testid="home-activity"]'), deferMount: true, view: 'overview', group: 'main',
        popover: s('The Manifest — your day', 'Every trip and booking for the day. Unassigned trips are flagged so nothing sails without a captain.', { side: 'top', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid^="home-slot-"]'), deferMount: true, view: 'overview', group: 'main',
        advanceOnSelector: '[data-testid="home-detail-sheet"]',
        popover: s('✨ Open a booking', "Tap any booking row to open its detail sheet. Go ahead — I'll wait.", { side: 'bottom', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="home-detail-boat-select"], [data-testid="home-detail-boat-readonly"]'), deferMount: true,
        popover: s('Assign the vessel', "Pick which asset runs this trip — only compatible, available ones show. This assignment powers capacity and the vessel's earnings report.", { side: 'left', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="home-detail-crew"], [data-testid="home-detail-crew-readonly"]'), deferMount: true,
        popover: s('Assign the crew', 'Only members whose ROLES qualify for this vessel appear — that\'s the crew gate you set on the asset. AI suggestions can auto-pick for you.', { side: 'left', align: 'start' }),
      },
      {
        element: '[data-testid="nav-checkin"]', view: 'checkin', group: 'customers',
        popover: s('Day-of: check-in (optional)', "Check-in is a choice — some companies run it, some don't. Turn it on and every customer ticket carries a QR: scan it with a barcode gun or your phone camera from this tab, or let guests self check-in from your venue QR. Every check-in is timestamped everywhere.", { side: 'right', align: 'start' }),
      },
      { popover: s('⚓ Day, run', 'Manifest → vessel → crew → check-in. Assignments feed payroll, reports and the audit Log book automatically.') },
    ],
  },
  {
    id: 'guide_promo_v1',
    title: 'Run a promo',
    blurb: 'Create a discount code, control it, track it in campaigns.',
    steps: [
      { popover: s('🏷️ Promos that pay for themselves', "Create a code, keep the kill switch handy, and close the loop in campaigns — every booking the code brings back gets tracked.") },
      {
        element: firstVisible('[data-testid="add-discount-code-btn"]'), deferMount: true, view: 'discounts', group: 'customers',
        advanceOnSelector: '[data-testid="discount-create-modal"]',
        popover: s('✨ Create a code — click it!', "Open the code builder. Percent or fixed amount, usage caps, expiry, per-service rules. I'll wait.", { side: 'bottom', align: 'end' }),
      },
      {
        element: firstVisible('[data-testid="discount-create-modal"]'), deferMount: true,
        popover: s('Set the terms', 'Name the code (SUMMER20 style), pick percent or fixed, cap the uses, set an expiry. Save it, then hit Next.', { side: 'left', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid^="toggle-wrap-"]'), deferMount: true, view: 'discounts', group: 'customers',
        popover: s('The kill switch', 'This toggle turns the code on and off — instantly, everywhere it\'s used. Flip it off after a promo ends and old links stop working on the spot. Try it: off, then back on.', { side: 'left', align: 'start' }),
      },
      {
        element: '[data-testid="nav-intelligence"]',
        popover: s('Close the loop in campaigns', "In the Intelligence Suite, attach this code when drafting a campaign — the AI weaves it into every ad, emails show it as a copy-ready chip, and every booking it brings back is attributed to that campaign.", { side: 'right', align: 'start' }),
      },
      { popover: s('⚓ Promo, tracked', "Code → campaign → bookings → attribution. You'll see exactly what the promo earned, not just what it cost.") },
    ],
  },
  {
    id: 'guide_walkins_v1',
    title: 'Walk-in customers',
    blurb: 'Same-day walk-ups: zero-notice services, kiosk, QR & waivers.',
    steps: [
      { popover: s('🚶 Walk-ins, handled', 'Dock traffic is money. One tab runs the whole walk-up: book, pay, sign the waiver — with three redundant ways to do it.') },
      {
        element: firstVisible('[data-testid="nav-services"]'), view: 'services', group: 'operations',
        popover: s('Prep: zero-notice services', "Walk-ins book NOW — so the services you sell at the door need Minimum notice set to 0 hours (edit the service → Scheduling). A 2h+ notice window hides same-moment slots.", { side: 'right', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="nav-walkIns"]'), view: 'walkIns', group: 'main',
        popover: s('The Walk-Ins tab', 'Everything in one place: the booking widget, kiosk mode, the QR customers scan, waiver signing and the signed-waiver log.', { side: 'right', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="walkins-widget"]'), deferMount: true,
        popover: s('Way 1 — staff assisted', 'This is the exact widget your website uses. Walk the customer through it: pay by card now, or send a payment link to their phone. Confirmations and waiver requests fire automatically.', { side: 'top', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="walkins-kiosk-btn"]'), deferMount: true,
        popover: s('Way 2 — kiosk mode', 'Open the kiosk on an iPad and hand it over — two big tabs let customers book, pay AND sign the waiver, fully self-serve.', { side: 'bottom', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="walkins-qr-btn"]'), deferMount: true,
        popover: s('Way 3 — their phone', "Download and print the QR for the dock. Customers scan and book on their own phone — no hardware needed. If one way fails, you've always got another.", { side: 'bottom', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="walkins-waiver-card"]'), deferMount: true,
        popover: s('Waivers on the spot', 'After checkout (or before boarding) they sign here — link or QR. Entering the booking confirmation code ties the signature to the ticket.', { side: 'top', align: 'start' }),
      },
      {
        element: firstVisible('[data-testid="walkins-signed-waivers"]'), deferMount: true,
        popover: s('Proof, logged', 'Every signature lands here instantly — the same log as the Waivers tab, with device fingerprint and audit trail.', { side: 'top', align: 'start' }),
      },
      { popover: s('⚓ Walk-up ready', 'Zero-notice service + Walk-Ins tab + QR at the dock = no customer walks away. Tickets, notifications and waivers all fire automatically.') },
    ],
  },
]);

// ─── Intelligence Suite how-to guides ───────────────────────────────────────
// Run inside the suite via IntelligenceShell's tour engine (`path`, `wizard`
// and `advanceOn` are supported there). Copy stays currency-neutral — the
// suite serves both USD and AED organizations.
const intelNavEl = (id) => firstVisible(`[data-testid="intelligence-nav-${id}"], [data-testid="drawer-intelligence-nav-${id}"]`);

export const getIntelGuides = () => ([
  {
    id: 'intel_guide_chartroom_v1',
    title: 'Read your Chartroom',
    blurb: 'Your reports — and the opportunities hiding in them.',
    steps: [
      { popover: s('🧭 The Chartroom', "Your business, charted. Everything in here builds itself from your bookings and website traffic — no setup, no spreadsheets. Let's learn to read the map.") },
      {
        element: intelNavEl('home'), path: '/intelligence',
        popover: s('Start at Home', 'The overview: headline performance and where to look next. Check it like a morning weather report.', { side: 'right', align: 'start' }),
      },
      {
        element: intelNavEl('asset-earnings'), path: '/intelligence/asset-earnings',
        popover: s('Asset Earnings', "What each vessel really earns — built from the trip assignments your crew makes every day. It's how you decide what to run more of, and what to retire.", { side: 'right', align: 'start' }),
      },
      {
        element: intelNavEl('service-sales-mix'), path: '/intelligence/service-sales-mix',
        popover: s('Revenue Mix', 'Which services carry the business and which just fill the calendar. Pricing and bundling decisions start here.', { side: 'right', align: 'start' }),
      },
      {
        element: intelNavEl('demand-calendar'), path: '/intelligence/demand-calendar',
        popover: s('Demand Center', "When demand peaks and dips. Quiet weekdays? That's a campaign or an off-peak rate waiting to happen.", { side: 'right', align: 'start' }),
      },
      {
        element: intelNavEl('customer-insights'), path: '/intelligence/customer-insights',
        popover: s('Customer Insights', 'Who books, who returns, who spends the most — the raw material your audiences are built from.', { side: 'right', align: 'start' }),
      },
      { popover: s('⚓ Read it weekly', "The Chartroom is read-only on purpose — it's your instruments, not your controls. The opportunities it surfaces get acted on next door, in Audiences and Campaigns.") },
    ],
  },
  {
    id: 'intel_guide_beacon_v1',
    title: 'Install & verify your Beacon',
    blurb: "Get the tag on your site — and prove it's working.",
    steps: [
      { popover: s('📡 The Beacon', "A tiny tag on your website that streams visitor activity into HELM Intelligence. It's what powers Website Analytics and keeps your audiences fresh — worth two minutes to set up properly.") },
      {
        element: firstVisible('[data-testid="beacon-live-strip"], [data-testid="beacon-setup"]'),
        deferMount: true,
        path: '/intelligence/website-analytics',
        popover: s('Install it here', "Your Beacon status and install snippet live right here. Copy the tag into your website — and if BookingFlows built or hosts your site, chances are it's already aboard.", { side: 'bottom', align: 'start' }),
      },
      {
        popover: s('🔍 Now verify it — for real', 'Install our free Chrome extension, <strong>BookingFlows Beacon Tender</strong>, then open any page of YOUR website. The Tender lights up and shows Beacon events firing in real time — that\'s your proof it\'s installed correctly.<br/><br/><a href="https://chromewebstore.google.com/detail/bookingflows-beacon-tende/lomkgfganpkdkeeeipjdngkcmhbbahkh" target="_blank" rel="noopener noreferrer" data-testid="beacon-tender-store-link" style="display:inline-block;padding:8px 14px;border-radius:10px;background:#1F2A5C;color:#ffffff;font-weight:600;text-decoration:none;">Get Beacon Tender — Chrome Web Store ↗</a>'),
      },
      { popover: s('⚓ Data, flowing', "Once the Beacon is aboard, Website Analytics starts filling within minutes and your audiences keep themselves fresh. Not seeing events in the Tender? Re-copy the snippet — or hail Ask Bosun.") },
    ],
  },
  {
    id: 'intel_guide_campaign_v1',
    title: 'Run a campaign',
    blurb: 'Draft real ads with AI, test them in your inbox, launch.',
    steps: [
      { popover: s('📣 Campaigns — where data pays you back', "Pick an audience, set a small daily budget, let the AI write the ads. Two stops are hands-on — you'll draft real ads and test them in your own inbox. Nothing sends until YOU hit Launch.") },
      {
        element: firstVisible('[data-testid="camp-kpi-balance"]'),
        deferMount: true,
        path: '/intelligence/campaigns',
        popover: s('Your ad credit', 'Campaigns spend from this balance — in your local currency, pay-per-message. Top up anytime, or set auto-recharge and forget it.', { side: 'bottom', align: 'start' }),
      },
      ...campaignWizardSteps(),
      {
        wizard: { open: false },
        popover: s('⚓ Ready when you are', "Saved as a draft — review it once more, then hit Launch. Delivery auto-weights toward the ads that earn clicks, and every booking it brings back is attributed right on the campaign card."),
      },
    ],
  },
]);

// Keep only steps whose target element is on screen (element-less steps,
// lazy-resolver steps, and deferred-mount steps — e.g. tab content or the
// Settings dialog — are always kept).
export const filterStepsToDom = (steps) =>
  steps.filter((step) => !step.element || step.deferMount || typeof step.element !== 'string' || document.querySelector(step.element));
